import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../../../config/axiosInstance';
import { OrderBy, OrderDirection } from '../utils/ordering';
import { Aggregators } from '../utils/aggregator';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Async thunks
export const querySessionsByStations = createAsyncThunk(
    'stations/querySessionsByStations',
    async (dateParamsArg, { getState, dispatch, rejectWithValue }) => {
        try {
            const state = getState();
            const dateParams = dateParamsArg || state.sessions.dateParams || {};
            const { limit, cursor, orderBy, orderDirection } = state.stations.sessionsByStations;
            const { termForApi } = state.stations.search || {};
            dispatch(loading());
            const response = await axios.get(`${API_BASE}/analytics/by-stations`, {
                params: {
                    ...dateParams,
                    limit,
                    cursor,
                    orderBy,
                    orderDirection,
                    stationName: termForApi || undefined,
                },
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const searchByStationNameAPI = createAsyncThunk(
    'stations/searchByStationNameAPI',
    async (_, { getState, dispatch, rejectWithValue }) => {
        try {
            const state = getState();
            const { aggregator } = state.sessions?.common?.dateParams || {};
            const { term } = state.stations.search || {};

            let groupParam = '';
            if (
                aggregator &&
                aggregator !== Object.keys(Aggregators).find(k => Aggregators[k] === Aggregators.singleAndMultiStation)
            ) {
                groupParam = `, "stationGroup":"${aggregator}"`;
            }

            const searchTerm = term?.toLowerCase();
            if (searchTerm) {
                dispatch(loading());
                dispatch(resetPagination());

                const response = await axiosInstance.get(
                    `${API_BASE}/users/list?filter=%7B"stationSchool":"${searchTerm}"${groupParam}%7D`
                );
                const stations = response.data;

                if (stations.length > 0) {
                    const aliases = stations.map(res => res.attributes.alias);
                    dispatch(searchByStationName(aliases));
                    await dispatch(querySessionsByStations());
                    return;
                }
                dispatch(resetStationList());
                return;
            }
            dispatch(resetSearch());
            await dispatch(querySessionsByStations());
            return;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const queryStationBreakdown = createAsyncThunk(
    'stations/queryStationBreakdown',
    async (stationId, { dateParamsArg, getState, rejectWithValue }) => {
        try {
            const dateParams = dateParamsArg || getState().sessions.dateParams || {};
            const response = await axiosInstance.get(`${API_BASE}/analytics/for-station`, {
                params: {
                    ...dateParams,
                    stationId,
                },
            });
            return { data: response.data, stationId };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// State
const initialState = {
    loading: false,
    error: false,
    search: {
        term: '',
        termForApi: '',
        stationAliasList: [],
    },
    sessionsByStations: {
        limit: 10,
        cursor: 0,
        allPages: 1,
        currentPage: 1,
        orderBy: OrderBy.sessions,
        orderDirection: OrderDirection.desc,
        breakdown: {},
    },
    loadingStationBreakdown: false,
};

const stationsSlice = createSlice({
    name: 'stations',
    initialState,
    reducers: {
        showError(state) {
            state.error = true;
        },
        changeLimit(state, action) {
            state.sessionsByStations.limit = action.payload;
            state.sessionsByStations.cursor = 0;
        },
        nextPage(state, action) {
            if (action.payload && state.sessionsByStations.currentPage < state.sessionsByStations.allPages) {
                state.sessionsByStations.currentPage += 1;
                // Calculate cursor for next page
                state.sessionsByStations.cursor = (state.sessionsByStations.currentPage - 1) * state.sessionsByStations.limit;
            }
        },
        prevPage(state, action) {
            if (action.payload && state.sessionsByStations.currentPage > 1) {
                state.sessionsByStations.currentPage -= 1;
                // Calculate cursor for previous page
                state.sessionsByStations.cursor = (state.sessionsByStations.currentPage - 1) * state.sessionsByStations.limit;
            }
        },
        closeStationBreakdown(state, action) {
            const stationId = action.payload;
            const { [stationId]: deletedStation, ...breakdown } = state.sessionsByStations.breakdown;
            state.sessionsByStations.breakdown = breakdown;
        },
        openStationBreakdown(state, action) {
            const stationId = action.payload;
            if (!state.sessionsByStations.breakdown[stationId]) {
                state.sessionsByStations.breakdown[stationId] = { loading: true };
            }
        },
        searchByStationName(state, action) {
            state.search.stationAliasList = action.payload;
        },
        toggleOrderBy(state, action) {
            const orderBy = action.payload;
            const { sessionsByStations } = state;
            sessionsByStations.orderBy = orderBy;
            sessionsByStations.cursor = 0;
            sessionsByStations.orderDirection = sessionsByStations.orderBy === orderBy
                ? sessionsByStations.orderDirection === OrderDirection.desc
                    ? OrderDirection.asc
                    : OrderDirection.desc
                : OrderDirection.desc;
        },
        updateSearchedStationName(state, action) {
            state.search.term = action.payload;
        },
        setSearchTermForApi(state, action) {
            state.search.termForApi = action.payload;
            // Reset to first page with initial limit when search changes
            state.sessionsByStations.cursor = 0;
            state.sessionsByStations.currentPage = 1;
            state.sessionsByStations.limit = initialState.sessionsByStations.limit;
            state.sessionsByStations.allPages = 1;
        },
        resetPagination(state) {
            state.sessionsByStations = { ...initialState.sessionsByStations };
        },
        resetSearch(state) {
            state.search = { ...initialState.search };
        },
        resetStationList(state) {
            state.sessionsByStations.breakdown = {};
            state.loading = false;
        },
        loading(state) {
            state.loading = true;
            state.sessionsByStations.cursor = 0;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(querySessionsByStations.pending, (state) => {
                state.loading = true;
            })
            .addCase(querySessionsByStations.fulfilled, (state, action) => {
                state.loading = false;
                const { data, meta: { cursor, pages, page } } = action.payload;

                // Only update cursor from API response if we're not paginating
                // (i.e., if currentPage is 1 or if we're doing initial load)
                const shouldUpdateCursor = state.sessionsByStations.currentPage === 1 ||
                    state.sessionsByStations.cursor === 0;

                state.sessionsByStations = {
                    ...state.sessionsByStations,
                    data,
                    cursor: shouldUpdateCursor ? (cursor ?? 0) : state.sessionsByStations.cursor,
                    allPages: pages ?? 1,
                    // Only update currentPage if it's not being changed by user pagination
                    currentPage: (state.sessionsByStations.currentPage || page) ?? 1,
                    breakdown: initialState.sessionsByStations.breakdown,
                };
            })
            .addCase(querySessionsByStations.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || true;
            })
            .addCase(queryStationBreakdown.pending, (state) => {
                state.loadingStationBreakdown = true;
            })
            .addCase(queryStationBreakdown.fulfilled, (state, action) => {
                state.loadingStationBreakdown = false;
                const { data, stationId } = action.payload;
                state.sessionsByStations.breakdown[stationId] = {
                    loading: false,
                    data: data.data,
                };
            })
            .addCase(queryStationBreakdown.rejected, (state, action) => {
                state.loadingStationBreakdown = false;
                state.error = action.payload || true;
            });
    },
});

export const {
    showError,
    changeLimit,
    nextPage,
    prevPage,
    closeStationBreakdown,
    openStationBreakdown,
    searchByStationName,
    toggleOrderBy,
    updateSearchedStationName,
    setSearchTermForApi,
    resetPagination,
    resetSearch,
    resetStationList,
    loading,
} = stationsSlice.actions;

// Additional API functions to match the old codebase
export const toggleOrderByApi = (orderBy) => (dispatch, getState) => {
    dispatch(toggleOrderBy(orderBy));
    const dateParams = getState().sessions.dateParams;
    dispatch(querySessionsByStations(dateParams));
    return null;
};

export const changeLimitApi = (newLimit) => (dispatch, getState) => {
    dispatch(changeLimit(newLimit));
    const dateParams = getState().sessions.dateParams;
    dispatch(querySessionsByStations(dateParams));
    return null;
};

// Thunk to toggle station breakdown and fetch data if needed (like old codebase)
export const toggleStationBreakdown = (stationId) => async (dispatch, getState) => {
    const { stations: { sessionsByStations: { breakdown } } } = getState();
    const station = breakdown[stationId];
    if (!station || Object.keys(station).length === 0) {
        await dispatch(queryStationBreakdown(stationId));
        dispatch(openStationBreakdown(stationId));
    } else {
        dispatch(closeStationBreakdown(stationId));
    }
};

export const nextPageApi = () => (dispatch, getState) => {
    const { stations: { sessionsByStations: { currentPage, allPages } }, sessions: { dateParams } } = getState();
    if (currentPage < allPages) {
        dispatch(nextPage(true));
        dispatch(querySessionsByStations(dateParams));
    }
    return null;
};

export const prevPageApi = () => (dispatch, getState) => {
    const { stations: { sessionsByStations: { currentPage } }, sessions: { dateParams } } = getState();
    if (currentPage > 1) {
        dispatch(prevPage(true));
        dispatch(querySessionsByStations(dateParams));
    }
    return null;
};

export default stationsSlice.reducer;

// Selectors
export const selectStationsState = (state) => state.sessions.stations;
export const selectSessionsByStations = (state) => state.stations.sessionsByStations;
export const selectStationSearch = (state) => state.stations.search;
export const selectStationsLoading = (state) => state.stations.loading;
export const selectStationsError = (state) => state.stations.error;
